<template>
  <BasePopup ref="popup" :title="popupTitle" class="popup--simple">
    <div v-if="accountStatus === null"><loading-spinner /></div>
    <div v-else>
      <div v-if="deletionRequestComplete" class="email-sent">
        We've just sent an email to {{ email }} to confirm your deletion. Click
        the link in that email. Your account will not be deleted until you do
        so. If you don't see the email, check your spam folder.
      </div>
      <div v-else>
        <AccountStatusInfo :account-status="accountStatus" />
        <div v-if="accountStatus.subscription_status !== 'active'">
          <form class="cancel-form">
            <div class="cancel-form__group--submit">
              <a
                class="cancel-form__close-btn btns btns--largest btns--font-large btns--full"
                @click="hide"
                >Keep my account</a
              >
              <a
                v-wait:disabled="'Deleting'"
                class="cancel-form__btn btns btns--largest btns--font-large btns--red btns--full"
                @click="requestDeletion"
              >
                <v-wait for="Deleting">
                  <template v-slot:waiting>
                    Deleting
                    <button-spinner />
                  </template>
                  <span>Delete account</span>
                </v-wait>
              </a>
            </div>
            <FormError :errors="validation.errors" field="delete_account" />
          </form>
        </div>
      </div>
    </div>
  </BasePopup>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'

import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import BasePopup from '@/components/ui/BasePopup.vue'
import { Mixpanel } from '@/analytics/mixpanel'
import { Sentry } from '@/services/sentry'
import { backend } from '@/services/backend'
import { AccountStatus } from '@/models/interfaces'
import { ValidationState } from '@/helpers/form'
import FormError from '@/components/ui/FormError.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import AccountStatusInfo from '@/app/components/account/AccountStatusInfo.vue'
import { auth } from '@/services/auth'

const wait = useWait()

const popup = ref<InstanceType<typeof BasePopup> | null>(null)

const accountStatus = ref<AccountStatus | null>(null)
const validation = ref(new ValidationState())
const deletionRequestComplete = ref(false)
const email = ref<string | null>(null)

onBeforeMount(() => {
  email.value = auth.getEmail()
})

async function show(): Promise<void> {
  popup.value?.show()

  if (accountStatus.value === null) {
    try {
      accountStatus.value = await backend.getAccountStatus()
    } catch (error) {
      // The error here is usually NetworkError or Timeout, but
      // we still capture all errors to Sentry to be aware if
      // error rate increases.
      Sentry.withScope(function (scope) {
        scope.setFingerprint(['account-status-loading-error'])
        Sentry.captureException(error)
      })
    }
  }
  Mixpanel.trackAccountDeleteClick()
}

function hide(): void {
  validation.value.reset()
  popup.value?.hide()
  Mixpanel.trackAccountDeleteCancel()
}

async function requestDeletion(event: Event): Promise<void> {
  event.preventDefault()
  validation.value.reset()
  try {
    wait.start('Deleting')
    await backend.requestAccountDeletion()
    deletionRequestComplete.value = true
    wait.end('Deleting')
  } catch (error) {
    wait.end('Deleting')
    Sentry.captureException(error)
    validation.value.showError(
      'delete_account',
      'Account deletion failed. Please try again.',
    )
  }
}

const popupTitle = computed(() => {
  return deletionRequestComplete.value
    ? 'Check Your Email to Delete'
    : 'Delete Account'
})

defineExpose({
  show,
  hide,
})
</script>

<style scoped lang="scss">
.email-sent {
  text-align: left;
}
</style>
